import {Clone} from "js-vextensions";
import {observer_mgl} from "mobx-graphlink";
import React, {useState} from "react";
import {Button, CheckBox, Column, Row, RowLR, Text, TextInput} from "react-vcomponents";
import {ShowMessageBox} from "react-vmessagebox";
import {AssertNotify, ES, Icon, RunInAction_Set, TextPlus} from "web-vcore";
import {EngineConfigVariant, VariantFieldOverride} from "../../../../Server/DB/engineConfigs/@EngineConfig/@Shared/EngineConfigVariant.js";
import {store} from "../../../../Store/index.js";
import {colors} from "../../../../Utils/UI/GlobalStyles.js";
import {EngineConfigPanels_SharedProps, ExtendEngineConfigPanelSharedProps} from "../ConfigPanel_Local.js";
import {MDIcon} from "../../../../Utils/UI/MDIcon.js";
import {GetPreOverrideValue} from "../../../../Engine/LiveSession/Components/VariantsComp/VariantUtils.js";
import {ErrToStr} from "../../../../Utils/General/Errors.js";

const GetSharedProps = (props: EngineConfigPanels_SharedProps)=>ExtendEngineConfigPanelSharedProps(props, c=>c.variants, (c, v)=>c.variants = v);

export const Variants_ConfigUI = observer_mgl((props: {remote?: boolean} & EngineConfigPanels_SharedProps)=>{
	const {client, enabled} = props;
	const sharedProps = GetSharedProps(props);
	const {config, ChangeConfig} = sharedProps;

	return (
		<Column mt={15} style={{background: "hsla(0,0%,100%,.1)", borderRadius: 5, padding: 5, color: colors.nightTint}}>
			<Row style={{fontSize: 18}}>Variants</Row>

			<VariantsUI {...sharedProps}/>
		</Column>
	);
});

export const VariantsUI = observer_mgl((props: {} & EngineConfigPanels_SharedProps)=>{
	const {enabled, client, config_full, ChangeConfig_Full} = props;
	const variants = config_full.variants.variants;
	const [selectedVariantInfo, setSelectedVariantInfo] = useState({name: "", index: 0});
	// find... by index (if name matches); else first name-match (if one exists); else entry at index (if in-bounds); else 0
	const selectedVariant_finalIndex =
		variants[selectedVariantInfo.index]?.name == selectedVariantInfo.name ? selectedVariantInfo.index :
		variants.findIndex(a=>a.name == selectedVariantInfo.name) != -1 ? variants.findIndex(a=>a.name == selectedVariantInfo.name) :
		selectedVariantInfo.index.KeepBetween(0, variants.length - 1);

	return (
		<>
			<Row mt={5}>
				<Text>Variants:</Text>
				<Row style={{flexWrap: "wrap", gap: 5, marginLeft: 5}}>
					{variants.map((variant, index)=>{
						return <Button key={index} p="2px 5px" text={`${index + 1}: ${variant.name}${!variant.enabled ? " [d]" : ""}`}
							style={ES(
								selectedVariant_finalIndex == index && {backgroundColor: "rgba(100,200,100,.5)"},
							)}
							onClick={()=>setSelectedVariantInfo({name: variant.name, index})}/>;
					})}
					<Button mdIcon="plus" enabled={enabled && !client} onClick={()=>{
						ChangeConfig_Full(c=>{
							c.variants.variants.push(new EngineConfigVariant({enabled: true, name: `Variant ${c.variants.variants.length + 1}`}));
						});
					}}/>
				</Row>
			</Row>
			{selectedVariant_finalIndex <= variants.length - 1 &&
			<VariantUI {...props} variant={variants[selectedVariant_finalIndex]} variantIndex={selectedVariant_finalIndex} setSelectedVariantInfo={setSelectedVariantInfo}/>}
		</>
	);
});

export const VariantUI = observer_mgl((props: {variant: EngineConfigVariant, variantIndex: number, setSelectedVariantInfo: (info: {name: string, index: number})=>void} & EngineConfigPanels_SharedProps)=>{
	const {variant, variantIndex, setSelectedVariantInfo, enabled, client, config_full, ChangeConfig_Full} = props;
	const uiState = store.main.engine.config;
	const ChangeVariant = (changerFunc: (variant: EngineConfigVariant)=>void)=>{
		ChangeConfig_Full(c=>{
			const newVariant = c.variants.variants[variantIndex];
			changerFunc(newVariant);
		});
	};
	const ChangeFieldOverrides = (fieldOverridesChangerFunc: (fieldOverrides: VariantFieldOverride[])=>void)=>{
		ChangeVariant(s=>fieldOverridesChangerFunc(s.fieldOverrides));
	};

	const sharedProps = props as EngineConfigPanels_SharedProps;
	return (
		<>
			<Row mt={5}>
				<Text>Name:</Text>
				<TextInput ml={5} enabled={enabled} value={variant.name} onChange={val=>{
					ChangeVariant(s=>s.name = val);
					setSelectedVariantInfo({name: val, index: variantIndex});
				}}/>
				<CheckBox ml={5} text="Enabled" enabled={enabled} value={variant.enabled} onChange={val=>ChangeVariant(s=>s.enabled = val)}/>
				<Button ml={5} mdIcon="content-copy" enabled={enabled && !client} onClick={()=>{
					RunInAction_Set(()=>uiState.variants_copiedVariant = Clone(variant));
				}}/>
				<Button ml={5} mdIcon="content-paste" enabled={enabled && !client} onClick={()=>{
					ShowMessageBox({
						title: "Replace variant?", cancelButton: true,
						message: ()=><Column>
							<Row>Permanently replace this variant with copied variant?</Row>
							<Row>{`Copied variant: "${uiState.variants_copiedVariant?.name}"`}</Row>
						</Column>,
						onOK: ()=>{
							ChangeConfig_Full(c=>{
								c.variants.variants[variantIndex] = Clone(uiState.variants_copiedVariant);
							});
						},
					});
				}}/>
				<Button ml={5} mdIcon="arrow-left" title="Move left" enabled={enabled && variantIndex > 0} onClick={()=>{
					ChangeConfig_Full(c=>{
						const newVariants = c.variants.variants;
						newVariants.Move(newVariants[variantIndex], variantIndex - 1);
						setSelectedVariantInfo({name: variant.name, index: variantIndex - 1});
					});
				}}/>
				<Button ml={5} mdIcon="arrow-right" title="Move right" enabled={enabled && variantIndex < config_full.variants.variants.length - 1} onClick={()=>{
					ChangeConfig_Full(c=>{
						const newVariants = c.variants.variants;
						newVariants.Move(newVariants[variantIndex], variantIndex + 1);
						setSelectedVariantInfo({name: variant.name, index: variantIndex + 1});
					});
				}}/>
				<Button ml={5} mdIcon="delete" enabled={enabled && !client} onClick={()=>{
					ShowMessageBox({
						title: "Delete variant?", cancelButton: true,
						message: `Permanently delete this variant?`,
						onOK: ()=>{
							ChangeConfig_Full(c=>{
								c.variants.variants.RemoveAt(variantIndex);
							});
						},
					});
				}}/>
				<TextPlus ml={5} info="Overrides $variantName in breath comp's tts-message templates.">Name for TTS:</TextPlus>
				<TextInput ml={5} enabled={enabled} style={{flex: 1}} value={variant.nameForTTS ?? ""} onChange={val=>ChangeVariant(s=>s.nameForTTS = val)}/>
			</Row>
			<Row mt={5}>
				<TextPlus info="Field-overrides are inherited from these variants (last override wins).">Inherit variants:</TextPlus>
				<Button ml={5} mdIcon="plus" enabled={enabled && !client} onClick={()=>{
					ChangeVariant(s=>(s.inheritVariants ?? (s.inheritVariants = [])).push(""));
				}}/>
				{(variant.inheritVariants ?? []).map((inheritName, index)=>{
					const resolved = inheritName.length > 0 && config_full.variants.variants.some(v=>v.name == inheritName);
					return (
						<Row key={index} ml={5}>
							<TextInput enabled={enabled && !client} value={inheritName} onChange={val=>{
								ChangeVariant(s=>s.inheritVariants[index] = val);
							}}/>
							<MDIcon ml={5} icon={resolved ? "check" : "close"} size={18} height="auto" style={{color: resolved ? "limegreen" : "red", alignItems: "center"}}/>
							<Button ml={5} mdIcon="delete" enabled={enabled && !client} onClick={()=>{
								ChangeVariant(s=>s.inheritVariants.RemoveAt(index));
							}}/>
						</Row>
					);
				})}
			</Row>
			<Row mt={5} mb={5}>
				<Text>Field overrides:</Text>
				<Button ml={5} mdIcon="plus" enabled={enabled && !client} onClick={()=>{
					ChangeFieldOverrides(a=>{
						a.push(new VariantFieldOverride({fieldPath: "", value: null}));
					});
				}}/>
			</Row>
			{variant.fieldOverrides.map((override, index)=>{
				return <FieldOverrideUI key={index} {...sharedProps} override={override} {...{index, variant, variantIndex}}/>;
			})}
		</>
	);
});

const FieldOverrideUI = observer_mgl((props: {override: VariantFieldOverride, index: number, variant: EngineConfigVariant, variantIndex: number} & EngineConfigPanels_SharedProps)=>{
	const {override, index, variant, variantIndex, enabled, client, config_full, ChangeConfig_Full} = props;

	const ChangeOverride = (changerFunc: (override: VariantFieldOverride)=>void)=>{
		ChangeConfig_Full(c=>{
			const newOverride = c.variants.variants[variantIndex].fieldOverrides[index];
			changerFunc(newOverride);
		});
	};

	// Compute the value that would be at this field path without the current variant's overrides
	// (base config value, then ancestors' overrides applied in chain order)
	const {value: preOverrideValue, source: preOverrideSource} = GetPreOverrideValue(config_full, variant, override.fieldPath);
	const isRedundant = JSON.stringify(override.value) === JSON.stringify(preOverrideValue);
	const redundantValueLabel_color = isRedundant ? (preOverrideSource === "base-config" ? "orange" : "yellow") : undefined;
	return (
		<Row mt={index == 0 ? 0 : 5}>
			<Row style={{flex: 70}}>
				<Text>Field:</Text>
				<TextInput ml={5} style={{flex: 1}} enabled={enabled} value={override.fieldPath} onChange={val=>ChangeOverride(s=>s.fieldPath = val)}/>
			</Row>
			<Row style={{flex: 30}}>
				<Text ml={5} style={redundantValueLabel_color != null ? {color: redundantValueLabel_color} : undefined} title={
					isRedundant ? `Redundant; this field already has this value from: ${preOverrideSource}.` : undefined
				}>Value:</Text>
				<TextInput ml={5} style={{flex: 1}} value={JSON.stringify(override.value)} onChange={val=>{
					try {
						const parsedValue = JSON.parse(val);
						ChangeOverride(s=>s.value = parsedValue);
					} catch (err) {
						AssertNotify(false, `Error parsing override value: ${ErrToStr(err)}`);
					}
				}}/>
				<Button ml={5} mdIcon="arrow-up" title="Move up" enabled={enabled && index > 0} onClick={()=>{
					ChangeConfig_Full(c=>{
						const newOverrides = c.variants.variants[variantIndex].fieldOverrides;
						newOverrides.Move(newOverrides[index], index - 1);
					});
				}}/>
				<Button ml={5} mdIcon="arrow-down" title="Move down" enabled={enabled && index < variant.fieldOverrides.length - 1} onClick={()=>{
					ChangeConfig_Full(c=>{
						const newOverrides = c.variants.variants[variantIndex].fieldOverrides;
						newOverrides.Move(newOverrides[index], index + 1);
					});
				}}/>
				<Button ml={5} mdIcon="delete" enabled={enabled && !client} onClick={()=>{
					ShowMessageBox({
						title: "Delete field-override?", cancelButton: true,
						message: `Permanently delete this field-override?`,
						onOK: ()=>{
							ChangeConfig_Full(c=>{
								c.variants.variants[variantIndex].fieldOverrides.RemoveAt(index);
							});
						},
					});
				}}/>
			</Row>
		</Row>
	);
});